import React from 'react';
import { useStore } from 'store/StoreContext';
import { BrowserRouter as Router, Route, Switch, RouteProps, Redirect, useLocation } from 'react-router-dom';
import { RoutePath, childNonOperablePaths } from 'consts/RoutePath';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './components/templates/override.module.scss';
import { Header } from './components/organisms/Header';
import { Footer } from './components/organisms/Footer';
import { LoginPage } from 'pages/LoginPage';
import { UserRegister1Page } from 'pages/UserRegister1Page';
import { UserRegister2Page } from 'pages/UserRegister2Page';
import { UserRegister3Page1 } from 'pages/UserRegister3Page1';
import { UserRegister3Page2 } from 'pages/UserRegister3Page2';
import { UserRegister4Page } from 'pages/UserRegister4Page';
import { UserRegister5Page } from 'pages/UserRegister5Page';
import { MyPage } from 'pages/MyPage';
import { ProfileEditPage } from 'pages/ProfileEditPage';
import { ProfileEditMailAddressPage } from 'pages/ProfileEditMailAddressPage';
import { ProfileEditMailAddressConfirmPage } from 'pages/ProfileEditMailAddressConfirmPage';
import { PasswordEditPage } from 'pages/PasswordEditPage';
import { TermsPage } from 'pages/TermsPage';
import { TermsCompletePage } from 'pages/TermsCompletePage';
import { ForgotPasswordPage } from 'pages/ForgotPasswordPage';
import { ForgotPasswordConfirmPage } from 'pages/ForgotPasswordConfirmPage';
import { ForgotPasswordUpdatePage } from 'pages/ForgotPasswordUpdatePage';
import { CommonLoginPage } from 'pages/CommonLoginPage';
import { RedirectLoginPage } from 'pages/RedirectLoginPage';
import { NotFoundPage } from 'pages/NotFoundPage';
import { ErrorPage } from 'pages/ErrorPage';
import { AgreementPage } from 'pages/AgreementPage';
import { AgreementEnPage } from 'pages/AgreementEnPage';
import { useCheckLogin } from 'hooks/UseCheckLogin';
import { MemberInfo } from 'pages/MemberInfo';
import { MemberRemove } from 'pages/MemberRemove';
import { MemberRemoveContactForm } from 'pages/MemberRemoveContactForm';
import { MemberUpdate } from 'pages/MemberUpdate';
import { UserAdd } from 'pages/UserAdd';
import { MemberInput } from 'pages/MemberInput';
import { MemberAdd } from 'pages/MemberAdd';
import { ChangeRepresentative } from 'pages/ChangeRepresentative';
import { GetOutFamily } from 'pages/GetOutFamily';
import { InvitationAgree } from 'pages/InvitationAgree';
import { LeaveFamily } from 'pages/LeaveFamily';
import { LeaveFamilyOnly } from 'pages/LeaveFamilyOnly';
import { LeaveFamilyTogether } from 'pages/LeaveFamilyTogether';
import { Payment } from 'pages/Payment';
import { PeriodicPayment } from 'pages/PeriodicPayment';
import { BulkPayment } from 'pages/BulkPayment';
import { BulkPeriodicPayment } from 'pages/BulkPeriodicPayment';
import { ForgotIdPage } from 'pages/ForgotIdPage';
import { ForgotIdConfirmPage } from 'pages/ForgotIdConfirmPage';
import { WithdrawPage } from 'pages/WithdrawPage';
import { WithdrawContactFormPage } from 'pages/WithdrawContactFormPage';
import { CardManagement } from 'pages/CardManagement';
import { AccountInfoPage } from 'pages/AccountInfoPage';
import { FamilyInfoPage } from 'pages/FamilyInfoPage';
import { EmailDeliveryPage } from 'pages/EmailDeliveryPage';
import { AttributeAgreementPage } from 'pages/AttributeAgreementPage';
import { BulkPaymentComplete } from 'pages/BulkPaymentComplete';
import { BulkPeriodicPaymentComplete } from 'pages/BulkPeriodicPaymentComplete';
import { RequiredProfileEditPage } from 'pages/RequiredProfileEditPage';

interface Props {}

const PrivateRoute: React.VFC<RouteProps> = ({ ...props }) => {
	const [store] = useStore();
	const { loading, isAuthenticated } = useCheckLogin();
	const search = useLocation().search;

	const query = new URLSearchParams(search);

	if (loading) {
		return (
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
		);
	}

	if (!isAuthenticated) {
		if (!!query.get('_gl')) {
			return <Redirect to={`${RoutePath.LOGIN}?_gl=${query.get('_gl')}`} />;
		} else {
			return <Redirect to={RoutePath.LOGIN} />;
		}
	}

	// ログインユーザが子供の場合、アクセスできないページにアクセスしていたらNotFoundへリダイレクト
	if (store.loginUserData.isChild && childNonOperablePaths.includes(props.path as string)) {
		return <Redirect to={RoutePath.NOT_FOUND} />;
	}

	return <Route {...props} />;
};

const LoginRoute: React.VFC<RouteProps> = ({ ...props }) => {
	const { loading, isAuthenticated } = useCheckLogin();
	const search = useLocation().search;

	const query = new URLSearchParams(search);

	if (loading) {
		return (
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
		);
	}

	if (isAuthenticated) {
		if (!!query.get('_gl')) {
			return <Redirect to={`${RoutePath.MY_PAGE}?_gl=${query.get('_gl')}`} />;
		} else {
			return <Redirect to={RoutePath.MY_PAGE} />;
		}
	}
	return <Route {...props} />;
};

const MainRouter = (_props: Props) => {
	return (
		<>
			<Router>
				<Header />
				<Switch>
					<LoginRoute exact path={RoutePath.LOGIN}>
						<LoginPage />
					</LoginRoute>
					<Route exact path={RoutePath.COMMON_LOGIN}>
						<CommonLoginPage />
					</Route>
					<Route exact path={RoutePath.REDIRECT_LOGIN}>
						<RedirectLoginPage />
					</Route>
					<Route exact path={RoutePath.USER_REGISTER_1}>
						<UserRegister1Page />
					</Route>
					<Route exact path={RoutePath.USER_REGISTER_2}>
						<UserRegister2Page />
					</Route>
					<Route exact path={RoutePath.USER_REGISTER_3_1}>
						<UserRegister3Page1 />
					</Route>
					<Route exact path={RoutePath.USER_REGISTER_3_2}>
						<UserRegister3Page2 />
					</Route>
					<Route exact path={RoutePath.USER_REGISTER_4}>
						<UserRegister4Page />
					</Route>
					<Route exact path={RoutePath.USER_REGISTER_5}>
						<UserRegister5Page />
					</Route>
					<Route exact path={RoutePath.TERMS}>
						<TermsPage />
					</Route>
					<Route exact path={RoutePath.TERMS_COMPLETE}>
						<TermsCompletePage />
					</Route>
					<Route exact path={RoutePath.FORGOT_PASSWORD}>
						<ForgotPasswordPage />
					</Route>
					<Route exact path={RoutePath.FORGOT_PASSWORD_CONFIRM}>
						<ForgotPasswordConfirmPage />
					</Route>
					<Route exact path={RoutePath.FORGOT_PASSWORD_UPDATE}>
						<ForgotPasswordUpdatePage />
					</Route>
					<PrivateRoute exact path={RoutePath.MY_PAGE}>
						<MyPage />
					</PrivateRoute>
					<PrivateRoute exact path={RoutePath.PROFILE_EDIT}>
						<ProfileEditPage />
					</PrivateRoute>
					<PrivateRoute exact path={RoutePath.PROFILE_EDIT_MAILADDRESS}>
						<ProfileEditMailAddressPage />
					</PrivateRoute>
					<PrivateRoute exact path={RoutePath.PROFILE_EDIT_MAILADDRESS_CONFIRM}>
						<ProfileEditMailAddressConfirmPage />
					</PrivateRoute>
					<PrivateRoute exact path={RoutePath.PASSWORD_EDIT}>
						<PasswordEditPage />
					</PrivateRoute>
					<Route exact path={RoutePath.ERROR}>
						<ErrorPage />
					</Route>
					<Route path={RoutePath.AGREEMENT}>
						<AgreementPage />
					</Route>
					<Route path={RoutePath.AGREEMENT_EN}>
						<AgreementEnPage />
					</Route>
					<PrivateRoute path={RoutePath.MEMBER_INFO}>
						<MemberInfo />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.MEMBER_REMOVE}>
						<MemberRemove />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.MEMBER_REMOVE_CONTACT_FORM}>
						<MemberRemoveContactForm />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.MEMBER_UPDATE}>
						<MemberUpdate />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.USER_ADD}>
						<UserAdd />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.MEMBER_INPUT}>
						<MemberInput />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.CHANGE_REPRESENTATIVE}>
						<ChangeRepresentative />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.GET_OUT_FAMILY}>
						<GetOutFamily />
					</PrivateRoute>
					<Route path={RoutePath.INVITATION_AGREE}>
						<InvitationAgree />
					</Route>
					<PrivateRoute path={RoutePath.LEAVE_FAMILY}>
						<LeaveFamily />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.LEAVE_FAMILY_ONLY}>
						<LeaveFamilyOnly />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.LEAVE_FAMILY_TOGETHER}>
						<LeaveFamilyTogether />
					</PrivateRoute>
					<Route path={RoutePath.PAYMENT}>
						<Payment />
					</Route>
					<Route path={RoutePath.PERIODIC_PAYMENT}>
						<PeriodicPayment />
					</Route>
					<Route path={RoutePath.BULK_PAYMENT}>
						<BulkPayment />
					</Route>
					<Route path={RoutePath.BULK_PERIODIC_PAYMENT}>
						<BulkPeriodicPayment />
					</Route>
					<Route path={RoutePath.BULK_PAYMENT_COMPLETE}>
						<BulkPaymentComplete />
					</Route>
					<Route path={RoutePath.BULK_PERIODIC_PAYMENT_COMPLETE}>
						<BulkPeriodicPaymentComplete />
					</Route>
					<Route path={RoutePath.FORGOT_ID}>
						<ForgotIdPage />
					</Route>
					<Route exact path={RoutePath.FORGOT_ID_CONFIRM}>
						<ForgotIdConfirmPage />
					</Route>
					<PrivateRoute path={RoutePath.WITHDRAW}>
						<WithdrawPage />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.WITHDRAW_CONTACT_FORM}>
						<WithdrawContactFormPage />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.CARD_MANAGEMENT}>
						<CardManagement />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.ACCOUNT_INFO}>
						<AccountInfoPage />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.FAMILY_INFO}>
						<FamilyInfoPage />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.MEMBER_ADD}>
						<MemberAdd />
					</PrivateRoute>
					<PrivateRoute path={RoutePath.EMAIL_DELIVERY}>
						<EmailDeliveryPage />
					</PrivateRoute>
					<Route path={RoutePath.ATTRIBUTE_AGREEMENT}>
						<AttributeAgreementPage />
					</Route>
					<Route path={RoutePath.REQUIRED_PROFILE_EDIT}>
						<RequiredProfileEditPage />
					</Route>
					<Route path="/">
						<NotFoundPage />
					</Route>
				</Switch>
				<Switch>
					<Footer />
				</Switch>
			</Router>
		</>
	);
};

export default MainRouter;
